import React, { useState, useEffect } from 'react';
import { 
  X, CreditCard, Banknote, ShieldCheck, CheckCircle, Plus, 
  Loader2, ArrowRight, History, Trophy, Ticket, Box
} from 'lucide-react';
import { db } from '../../services/firebase';
import { 
  doc, updateDoc, increment, collection, addDoc, 
  serverTimestamp, query, orderBy, limit, getDocs, where, onSnapshot 
} from 'firebase/firestore';

// 🟢 1. FIXED PAYSTACK HOOK (Prevents React 'removeChild' crash)
const usePaystack = () => {
    const [loaded, setLoaded] = useState(false);
    
    useEffect(() => {
        if (document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]')) {
            setLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://js.paystack.co/v1/inline.js';
        script.async = true;
        script.onload = () => setLoaded(true);
        document.body.appendChild(script);
    }, []);
    
    return loaded;
};

declare global { interface Window { PaystackPop: any; } }

interface WalletTerminalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: any; 
}

export const WalletTerminal: React.FC<WalletTerminalProps> = ({
  isOpen, onClose, currentUser
}) => {
  const paystackLoaded = usePaystack();

  const PAYSTACK_PUBLIC_KEY = 'pk_live_7985b67599e69e640b5f64f870c93b8c77208617'; 

  const [balance, setBalance] = useState<number>(0);
  const [topUpAmount, setTopUpAmount] = useState<number | ''>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  const [leftTab, setLeftTab] = useState<'history' | 'vault'>('history');
  const [transactions, setTransactions] = useState<any[]>([]);
  const [rewards, setRewards] = useState<any[]>([]);
  const [loadingRewards, setLoadingRewards] = useState(false);

  const userId = currentUser?.uid || currentUser?.id;

  useEffect(() => {
    if (!isOpen || !userId) return;

    const userRef = doc(db, 'users', userId);
    const unsubscribeUser = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            setBalance(data.cred ?? data.credits ?? 0);
        }
    });

    const fetchHistoryAndVault = async () => {
        try {
            const txQuery = query(
                collection(db, 'users', userId, 'transactions'),
                limit(10)
            );
            const txSnap = await getDocs(txQuery);
            const txData = txSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            txData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setTransactions(txData);

            setLoadingRewards(true);
            const rewardsQuery = query(
                collection(db, 'rewards'),
                where('userId', '==', userId)
            );
            const rewardsSnap = await getDocs(rewardsQuery);
            const rewardsData = rewardsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            rewardsData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setRewards(rewardsData);
            setLoadingRewards(false);
        } catch (error) {
            console.error("Wallet Fetch Error:", error);
            setLoadingRewards(false);
        }
    };

    fetchHistoryAndVault();
    setPaymentSuccess(false);
    setTopUpAmount('');

    return () => unsubscribeUser();
  }, [isOpen, userId]);

  const handleTopUp = () => {
      const amount = Number(topUpAmount);
      if (!amount || amount < 10) {
          alert("System: Minimum top-up is ZAR 10.");
          return;
      }

      if (!paystackLoaded) {
          alert("System: Payment gateway offline. Try again.");
          return;
      }

      setIsProcessing(true);

      const handler = window.PaystackPop.setup({
          key: PAYSTACK_PUBLIC_KEY,
          email: currentUser?.email || 'user@scoop.app',
          amount: amount * 100,
          currency: 'ZAR',
          ref: `CRD_TOPUP_${Math.floor((Math.random() * 1000000000) + 1)}`,
          callback: function(response: any) {
              if (response.status === 'success') {
                  fulfillPurchase(amount, response.reference);
              } else {
                  setIsProcessing(false);
                  alert("Transaction Failed.");
              }
          },
          onClose: function() {
              setIsProcessing(false);
          }
      });

      handler.openIframe();
  };

  const fulfillPurchase = async (purchasedAmount: number, reference: string) => {
      if (!userId) return;

      try {
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, { cred: increment(purchasedAmount) });

          await addDoc(collection(db, 'users', userId, 'transactions'), {
              type: 'credit_purchase',
              amount: purchasedAmount,
              reference: reference,
              createdAt: serverTimestamp(),
              status: 'completed'
          });

          setPaymentSuccess(true);
          setIsProcessing(false);

          setTimeout(() => {
              setPaymentSuccess(false);
              setTopUpAmount('');
          }, 3000);

      } catch (error) {
          console.error("Fulfillment Error:", error);
          alert("Payment received but database update failed. Contact support with reference: " + reference);
          setIsProcessing(false);
      }
  };

  const handleClaim = async (reward: any) => {
      if (!userId || reward.status === 'claimed') return;
      setIsProcessing(true);

      try {
          if (reward.title === 'Hype Mystery Box') {
              const prizes = [
                  { type: 'credits', title: '500 Credits (Jackpot!)', amount: 500 },
                  { type: 'voucher', title: 'Free Shipping Voucher' },
                  { type: 'voucher', title: '10% Off Discount Code' },
                  { type: 'item', title: 'Exclusive Scoop Keychain' }
              ];
              
              const wonPrize = prizes[Math.floor(Math.random() * prizes.length)];
              const wonPrizeTitle = wonPrize.title;
              const wonPrizeAmount = wonPrize.amount || 0;
              
              if (wonPrize.type === 'credits') {
                  await updateDoc(doc(db, 'users', userId), { cred: increment(wonPrizeAmount) });
                  alert(`🎁 UNBOXED: You won ${wonPrizeTitle}! They have been added to your balance.`);
              } else if (wonPrize.type === 'voucher') {
                  await updateDoc(doc(db, 'users', userId), { activeVoucher: wonPrizeTitle });
                  alert(`🎁 UNBOXED: You won a ${wonPrizeTitle}! It is now active for your next checkout.`);
              } else {
                  await addDoc(collection(db, 'claims'), {
                      userId, rewardId: reward.id, item: wonPrizeTitle, status: 'pending', createdAt: serverTimestamp()
                  });
                  alert(`🎁 UNBOXED: You won a ${wonPrizeTitle}! Our team will ship this to your address.`);
              }
          } 
          else if (reward.rewardType === 'voucher') {
              await updateDoc(doc(db, 'users', userId), {
                  activeVoucher: reward.title 
              });
              alert(`Success: ${reward.title} activated! It will apply at your next checkout.`);
          } 
          else {
              await addDoc(collection(db, 'claims'), {
                  userId, rewardId: reward.id, item: reward.title, status: 'pending', createdAt: serverTimestamp()
              });
              alert("Gear Secured: Our team will ship this to your primary address on file.");
          }

          await updateDoc(doc(db, 'rewards', reward.id), {
              status: 'claimed',
              claimedAt: serverTimestamp()
          });

          const rewardsQuery = query(
              collection(db, 'rewards'),
              where('userId', '==', userId)
          );
          const rewardsSnap = await getDocs(rewardsQuery);
          const rewardsData = rewardsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          rewardsData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          setRewards(rewardsData);
          
      } catch (error) {
          console.error("Claim Error:", error);
          alert("System Error: Could not process claim.");
      } finally {
          setIsProcessing(false);
      }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] bg-black/90 backdrop-blur-md flex flex-col lg:flex-row items-end lg:items-center justify-center animate-in fade-in duration-300">
      
      <div className="w-full h-[95vh] lg:w-[1100px] lg:h-[80vh] bg-black lg:border-2 border-zinc-800 flex flex-col lg:flex-row overflow-hidden shadow-[0_-20px_50px_rgba(0,0,0,0.5)] lg:shadow-2xl relative rounded-t-[2.5rem] lg:rounded-none mt-auto lg:mt-0">
        
        {/* CLOSE BUTTON */}
        <button onClick={onClose} className="absolute top-4 right-4 lg:top-6 lg:right-6 z-50 w-10 h-10 lg:w-auto lg:h-auto lg:p-3 flex items-center justify-center bg-zinc-900 lg:bg-white text-white lg:text-black border border-zinc-700 lg:border-black hover:bg-white hover:text-black transition-colors shadow-2xl rounded-full lg:rounded-none">
            <X size={18} strokeWidth={2.5} className="lg:w-5 lg:h-5"/>
        </button>

        {/* LEFT COLUMN: LEDGER */}
        <div className="w-full lg:w-1/2 h-[45vh] lg:h-full bg-zinc-950 flex flex-col border-b lg:border-b-0 lg:border-r border-zinc-800 shrink-0 pt-4 lg:pt-0">
            <div className="p-4 lg:p-8 flex-1 flex flex-col overflow-hidden">
                
                {/* Balance Display - Smaller */}
                <div className="border border-zinc-800 bg-black p-4 relative overflow-hidden group mb-3 rounded-2xl lg:rounded-none shrink-0">
                    <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 1px, transparent 10px)' }}></div>
                    <p className="text-[8px] font-mono text-zinc-500 uppercase tracking-[0.3em] mb-1 relative z-10 flex items-center gap-2">
                        <Banknote size={11}/> Available Credits
                    </p>
                    <h1 className="text-4xl lg:text-6xl font-black italic tracking-tighter text-white relative z-10 leading-none">
                        {balance} <span className="text-base lg:text-xl not-italic font-mono text-zinc-600">CRD</span>
                    </h1>
                </div>

                {/* TABS - Smaller */}
                <div className="flex border-b border-zinc-800 mb-2 shrink-0">
                    <button 
                        onClick={() => setLeftTab('history')} 
                        className={`pb-2 text-[8px] font-black uppercase tracking-widest flex-1 border-b-2 transition-colors flex items-center justify-center gap-1.5 ${leftTab === 'history' ? 'border-white text-white' : 'border-transparent text-zinc-600 hover:text-zinc-400'}`}
                    >
                        <History size={11}/> Activity
                    </button>
                    <button 
                        onClick={() => setLeftTab('vault')} 
                        className={`pb-2 text-[8px] font-black uppercase tracking-widest flex-1 border-b-2 transition-colors flex items-center justify-center gap-1.5 ${leftTab === 'vault' ? 'border-yellow-500 text-yellow-500' : 'border-transparent text-zinc-600 hover:text-yellow-500/50'}`}
                    >
                        <Trophy size={11}/> Vault {rewards.length > 0 && `(${rewards.length})`}
                    </button>
                </div>

                {/* TAB CONTENT - Smaller items */}
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-hide pb-safe space-y-2">
                    
                    {leftTab === 'history' && (
                        transactions.length === 0 ? (
                            <div className="text-zinc-600 font-mono text-[8px] uppercase tracking-widest py-4 text-center border border-dashed border-zinc-800 rounded-xl lg:rounded-none">No recent transactions.</div>
                        ) : (
                            transactions.map(tx => (
                                <div key={tx.id} className="flex justify-between items-center p-2.5 border border-zinc-800 bg-black rounded-xl lg:rounded-none">
                                    <div className="flex items-center gap-3">
                                        <div className="w-7 h-7 border border-zinc-700 bg-zinc-900 flex items-center justify-center shrink-0 rounded-full lg:rounded-none">
                                            {tx.type === 'credit_purchase' ? <Plus size={12} className="text-white"/> : <Banknote size={12} className="text-zinc-600"/>}
                                        </div>
                                        <div>
                                            <p className="text-[9px] font-black text-white uppercase tracking-widest">{tx.type === 'credit_purchase' ? 'Top-Up' : 'Expenditure'}</p>
                                            <p className="text-[7px] font-mono text-zinc-500 uppercase tracking-widest mt-0.5">Ref: {tx.reference || tx.id.slice(0,6)}</p>
                                        </div>
                                    </div>
                                    <div className={`text-xs font-black font-mono ${tx.type === 'credit_purchase' ? 'text-green-400' : 'text-zinc-500'}`}>
                                        {tx.type === 'credit_purchase' ? '+' : '-'}{tx.amount}
                                    </div>
                                </div>
                            ))
                        )
                    )}

                    {leftTab === 'vault' && (
                        loadingRewards ? (
                            <div className="flex flex-col items-center justify-center py-6 text-zinc-500">
                                <Loader2 className="animate-spin mb-2" size={18} />
                                <span className="text-[8px] font-mono uppercase tracking-widest">Decrypting Vault...</span>
                            </div>
                        ) : rewards.length === 0 ? (
                            <div className="text-zinc-600 font-mono text-[8px] uppercase tracking-widest py-6 text-center flex flex-col items-center border border-dashed border-zinc-800 rounded-xl lg:rounded-none bg-black">
                                <Box size={24} className="mb-2 text-zinc-700" />
                                Vault is Empty<br/><span className="text-[7px] mt-1 block opacity-50">Win games to secure assets.</span>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-2">
                                {rewards.map(reward => (
                                    <div key={reward.id} className={`flex justify-between items-center p-2.5 border transition-all rounded-xl lg:rounded-none ${reward.status === 'claimed' ? 'bg-zinc-900 border-zinc-800 opacity-50' : 'bg-black border-zinc-800 hover:border-zinc-600 group'}`}>
                                        <div className="flex items-center gap-3">
                                            <div className={`w-7 h-7 border flex items-center justify-center shrink-0 rounded-full lg:rounded-none ${reward.rewardType === 'voucher' ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400' : 'border-yellow-500/30 bg-yellow-500/10 text-yellow-500'}`}>
                                                {reward.rewardType === 'voucher' ? <Ticket size={12}/> : <Box size={12}/>}
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-black text-white uppercase tracking-widest">{reward.title}</p>
                                                <p className="text-[7px] font-mono text-zinc-500 uppercase tracking-widest mt-0.5">
                                                    {reward.status === 'claimed' ? 'Status: Applied' : `Via ${reward.sourceGame || 'System'}`}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <button 
                                            disabled={reward.status === 'claimed' || isProcessing}
                                            onClick={() => handleClaim(reward)}
                                            className={`px-3 py-1.5 text-[8px] font-black uppercase tracking-widest rounded-lg lg:rounded-none transition-colors ${
                                                reward.status === 'claimed' 
                                                ? 'bg-zinc-800 text-zinc-500' 
                                                : reward.rewardType === 'voucher' ? 'bg-cyan-500 text-black hover:bg-cyan-400' : 'bg-yellow-500 text-black hover:bg-yellow-400'
                                            }`}
                                        >
                                            {isProcessing ? <Loader2 size={8} className="animate-spin" /> : reward.status === 'claimed' ? 'Secured' : 'Claim'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                </div>
            </div>
        </div>

        {/* RIGHT COLUMN: TOP-UP TERMINAL */}
        <div className="flex-1 flex flex-col h-[50vh] lg:h-full relative overflow-y-auto bg-black pt-2 lg:pt-0">
            <div className="p-4 lg:p-8 flex-1 flex flex-col">
                
                <div className="border-b border-zinc-800 pb-3 mb-4 mt-1 lg:mt-0">
                    <h3 className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                        <CreditCard size={13} /> Top-Up Terminal
                    </h3>
                </div>

                {paymentSuccess ? (
                    <div className="flex-1 flex flex-col items-center justify-center animate-in zoom-in-95 duration-300">
                        <CheckCircle size={56} className="text-white mb-4" strokeWidth={1} />
                        <h2 className="text-3xl lg:text-4xl font-black text-white uppercase italic tracking-tighter mb-2 text-center">Funds<br/>Cleared</h2>
                        <div className="border border-white px-4 py-2 font-mono text-[8px] text-white uppercase tracking-widest text-center mt-2 rounded-xl lg:rounded-none">
                            Added: +{topUpAmount} CRD
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col">
                        <p className="text-zinc-400 font-mono text-[8px] uppercase tracking-widest leading-relaxed mb-4">
                            Convert ZAR to Scoop Credits. 1 CRD = R1.
                        </p>

                        {/* Quick Select - Smaller */}
                        <div className="grid grid-cols-3 gap-2 mb-4">
                            {[50, 100, 500].map((amt) => (
                                <button 
                                    key={amt}
                                    onClick={() => setTopUpAmount(amt)}
                                    className={`py-3 border-2 rounded-xl lg:rounded-none font-bold text-sm transition-colors ${topUpAmount === amt ? 'bg-white border-white text-black shadow-lg' : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-500'}`}
                                >
                                    +{amt}
                                </button>
                            ))}
                        </div>

                        {/* Custom Amount - Smaller */}
                        <div className="mb-4 border-b-2 border-zinc-800 focus-within:border-white transition-colors pb-1.5">
                            <label className="text-[8px] font-mono text-zinc-500 uppercase tracking-[0.3em] block mb-1">Custom Amount (ZAR)</label>
                            <div className="flex items-center gap-3">
                                <span className="text-2xl font-black text-zinc-600">R</span>
                                <input 
                                    type="number" 
                                    min="10"
                                    placeholder="0"
                                    value={topUpAmount}
                                    onChange={(e) => setTopUpAmount(e.target.value ? Number(e.target.value) : '')}
                                    className="bg-transparent text-3xl lg:text-4xl font-black text-white italic tracking-tighter outline-none w-full placeholder:text-zinc-800"
                                />
                            </div>
                        </div>

                        {/* Pay Button - Moved UP from bottom */}
                        <div className="pt-3 pb-2">
                            <div className="flex justify-between items-end mb-3">
                                <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-[0.2em]">Total</span>
                                <span className="text-xl font-black text-white">R {topUpAmount || '0'}</span>
                            </div>

                            <button 
                                onClick={handleTopUp}
                                disabled={isProcessing || !topUpAmount || topUpAmount < 10}
                                className="w-full py-3.5 border-2 border-white bg-white text-black font-black uppercase tracking-[0.15em] text-xs hover:bg-zinc-200 transition-transform active:scale-[0.98] flex items-center justify-center gap-2 shadow-2xl disabled:opacity-50 rounded-xl lg:rounded-none"
                            >
                                {isProcessing ? <Loader2 className="animate-spin" size={16} /> : <>Pay with Card <CreditCard size={14}/></>}
                            </button>

                            <div className="flex items-center justify-center gap-2 mt-2 text-[7px] font-mono text-zinc-600 uppercase tracking-[0.2em]">
                                <ShieldCheck size={10} /> Protected by Paystack
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>

      </div>
    </div>
  );
};